import { useCallback } from 'react';
import UploadIcon from '@icons/UploadIcon/UploadIcon';
import { useDropzone } from 'react-dropzone';
import { FileTag } from '@components/UploadForm/ui/FileTag/FileTag';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast';
import { CaseFile } from '@api/document/model/types';
import { formatFileSize } from '@utils/utils.ts';
import {
  ALLOWED_AUDIO_EXTENSIONS,
  ALLOWED_FILE_EXTENSIONS,
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_AUDIO_TEXT_EXTENSIONS,
  FILE_SIZE_LIMIT,
} from '@static/staticData.ts';
import { DocumentGeneratorInstanceVersionFile } from '@app/types/document-instance';
import Loader from '@components/Loader/ui/Loader';
import styles from './UploadForm.module.scss';

interface UploadFormProps {
  files?:
    | File[]
    | CaseFile[]
    | SectionFile[]
    | DocumentGeneratorInstanceVersionFile[];
  isImage?: boolean;
  isAudio?: boolean;
  isAudioText?: boolean;
  multiple?: boolean;
  onFileUpload?: (file: File[]) => void;
  onFileAction?: (type: 'upload' | 'remove', data?: any) => void;
  sizeLimit?: number;
  className?: string;
  fileBlockClassname?: string;
  isLoading?: boolean;
  loadingText?: string;
  disabled?: boolean;
}

function UploadForm(props: UploadFormProps) {
  const {
    files,
    sizeLimit = FILE_SIZE_LIMIT,
    isImage,
    isAudio,
    isAudioText,
    multiple = true,
    onFileUpload,
    onFileAction,
    fileBlockClassname,
    isLoading = false,
    loadingText = 'Uploading files...',
    disabled = false,
  } = props;
  const throwError = (errorMessage: string) => {
    toast(({ id }) => (
      <Toast id={id} type="error" title="Error" text={errorMessage} />
    ));
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!multiple && acceptedFiles.length > 1) {
        throwError('You can upload only one file');
        return;
      }

      const filteredFiles: File[] = acceptedFiles.filter((file) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (file.size > sizeLimit) {
          throwError(`${file.name} file too long`);
          return false;
        }

        if (!fileExtension) {
          throwError('Incorrect file type');
          return false;
        }

        if (
          (isImage && !ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) ||
          (isAudio && !ALLOWED_AUDIO_EXTENSIONS.includes(fileExtension)) ||
          (isAudioText &&
            !ALLOWED_AUDIO_TEXT_EXTENSIONS.includes(fileExtension)) ||
          (!isImage &&
            !isAudio &&
            !isAudioText &&
            !ALLOWED_FILE_EXTENSIONS.includes(fileExtension))
        ) {
          const allowedExtensions = isImage
            ? ALLOWED_IMAGE_EXTENSIONS.join(', ')
            : isAudio
              ? ALLOWED_AUDIO_EXTENSIONS.join(', ')
              : isAudioText
                ? ALLOWED_AUDIO_TEXT_EXTENSIONS.join(', ')
                : ALLOWED_FILE_EXTENSIONS.join(', ');

          throwError(`Only ${allowedExtensions} files are allowed.`);
          return false;
        }

        return true;
      });

      // Upload whatever passed. This previously required every dropped file to
      // be valid, so a folder containing one unsupported file uploaded nothing
      // at all, and the user was told only about the file that was rejected.
      // Each rejection already raises its own message above.
      if (onFileUpload && filteredFiles.length > 0) {
        onFileUpload(filteredFiles);
      }
    },
    [onFileUpload, sizeLimit, isImage, isAudio, isAudioText, multiple]
  );

  const getFileTypesText = () => {
    if (isImage) {
      return 'JPG, JPEG, PNG';
    }
    if (isAudio) {
      return 'MP3, WAV, OGG';
    }
    if (isAudioText) {
      return 'MP3, WAV, OGG, DOC, DOCx, PDF';
    }
    return 'DOC, DOCx, PDF';
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled,
  });

  const handleDisabledClick = () => {
    if (disabled) {
      toast(({ id }) => (
        <Toast
          id={id}
          type="warning"
          title="Read-only Files"
          text="These files are from the selected case and cannot be modified."
        />
      ));
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.uploadWrapper}>
        <div
          className={styles.uploadForm}
          {...(disabled ? { onClick: handleDisabledClick } : getRootProps())}
        >
          {isLoading ? (
            <div>
              <Loader />
              <p className={styles.loaderText}>{loadingText}</p>
            </div>
          ) : (
            <>
              <div className={styles.icon}>
                <div className={styles.inner}>
                  <UploadIcon />
                </div>
              </div>
              <input {...getInputProps()} />
              <div className={styles.text}>
                <p>
                  <span>Click to upload</span> or drag and drop
                  <br />
                  {getFileTypesText()} (max. {formatFileSize(sizeLimit)})
                </p>
              </div>
            </>
          )}
        </div>
        <div className={fileBlockClassname || styles.files}>
          {files?.map((file, index) => (
            <FileTag
              key={index}
              file={file}
              removeFile={
                disabled
                  ? undefined
                  : (data) => {
                      if (onFileAction) {
                        onFileAction('remove', data);
                      }
                    }
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default UploadForm;
